/**
 * The Explore publish, observed. Phase 2 (increment 3): the server's ack to
 * the "Publish to Explore" toggle is no longer publication — it STAGES a
 * candidate the server checks first, and the plan reaches Explore (or does
 * not) minutes later. So `plan_shared{method: 'publish_explore'}` — which
 * feeds the distribution test's share/fork number — fires on OBSERVED
 * promotion, once per (itinerary, promoted_at), remembered in localStorage so
 * a revisit cannot double-count. Web fired nothing for this share before; the
 * mobile toggle fired on the ack. Both platforms move here, in one slice, or
 * the funnel double-counts one and undercounts the other.
 *
 * Once-only, made robust three ways: the seen set is a SET of promoted_at
 * values (append-only), so an out-of-order older response cannot regress it
 * and re-fire; the read-check-write runs under a Web Lock keyed on the
 * itinerary, so two tabs cannot both read "unseen"; and anything that cannot
 * be made once-only — storage unavailable, OR a browser without Web Locks,
 * where two tabs could both read "unseen" before either wrote — counts as
 * SEEN: an undercount, never a double count. Web Locks ship in every current
 * browser (Chrome 69+, Firefox 96+, Safari 15.4+); the fallback is the
 * long-dead ones, and a second coordination mechanism for them would carry
 * its own races.
 */
import { checkShareStatus, type PublishStatus } from '../services/plansApi';
import { trackEvent } from '../hooks/useAnalytics';

const PENDING_KEY = (id: number) => `publish_pending:${id}`;
const SEEN_KEY = (id: number) => `publish_seen:${id}`;

/** 10 s apart for the first minute, then 30 s, for five minutes in all. */
export const POLL_SCHEDULE_MS: readonly number[] = [
  ...Array(6).fill(10_000), ...Array(8).fill(30_000),
];

const storage = {
  get(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  /** A read that can say it FAILED — `get` folds a throw into "absent",
   *  which is the wrong answer for the seen store: absent means "never
   *  reported", a failed read means "cannot know". */
  read(key: string): { ok: true; value: string | null } | { ok: false } {
    try { return { ok: true, value: localStorage.getItem(key) }; } catch { return { ok: false }; }
  },
  set(key: string, value: string): boolean {
    try { localStorage.setItem(key, value); return true; } catch { return false; }
  },
  remove(key: string): void {
    try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
  },
  // No availability probe: a failed READ is a null seen set (below) and a
  // failed WRITE returns false at the gate, so nothing a probe could catch
  // gets past them — while a one-byte probe can succeed where the real,
  // larger write fails, and it fired a cross-tab `storage` event per
  // observation for nothing.
};

type Locks = { request: <R>(n: string, cb: () => Promise<R>) => Promise<R> };

/** The browser's Web Locks, or undefined where there are none. */
function webLocks(): Locks | undefined {
  const locks = (typeof navigator !== 'undefined' ? (navigator as { locks?: Locks }).locks : undefined);
  return locks && typeof locks.request === 'function' ? locks : undefined;
}

/** The set of promoted_at values already reported for this itinerary, or
 *  NULL when it cannot be established — the read threw, the stored value is
 *  not JSON, not an array, or an array holding anything this code never
 *  writes (a non-string). Null is not "empty": an empty set
 *  means "never reported" and lets the event fire; null must suppress it,
 *  because a store that cannot be read (round 478: `getItem` throwing while
 *  `setItem` still works) would otherwise be rewritten from scratch and the
 *  same promotion reported again. An undercount, never a double. */
function seenSet(itineraryId: number): Set<string> | null {
  const read = storage.read(SEEN_KEY(itineraryId));
  if (!read.ok) return null;
  if (read.value === null) return new Set();
  let parsed: unknown;
  try { parsed = JSON.parse(read.value); } catch { return null; }
  if (!Array.isArray(parsed) || !parsed.every((x): x is string => typeof x === 'string')) return null;
  return new Set(parsed);
}

/** What one status read returned — the shape the surfaces render from. */
export interface ShareStatus { shared: boolean; edit_count: number; publish: PublishStatus }
type StatusListener = (status: ShareStatus) => void;
const listeners = new Map<number, Set<StatusListener>>();

/** Increment 5, decision 7: a surface that wants the outcome line to follow
 *  every poll registers here. The listener is invoked LAST inside
 *  `observePublish` — after the fact branch and the marker decision — inside
 *  its own try/catch, with the return value unchanged, so a throwing listener
 *  can neither escape into `watchPublish`'s per-poll catch (which would skip
 *  the terminal return and run the poll to the end of the schedule) nor
 *  change what the observation did. Returns the unsubscribe. */
export function onPublishStatus(itineraryId: number, listener: StatusListener): () => void {
  let set = listeners.get(itineraryId);
  if (!set) { set = new Set(); listeners.set(itineraryId, set); }
  set.add(listener);
  return () => {
    const current = listeners.get(itineraryId);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(itineraryId);
  };
}

function notifyStatus(itineraryId: number, status: ShareStatus): void {
  const set = listeners.get(itineraryId);
  if (!set) return;
  for (const listener of Array.from(set)) {
    try { listener(status); } catch { /* a surface's bug is not the observation's */ }
  }
}

let markerSerial = 0;

/** Call after a public toggle the server acknowledged. The value is a
 *  GENERATION: an observer that started under an older marker clears only
 *  that one (below), so a delayed terminal response for an old publish cannot
 *  erase the marker of a newer one (external round 479). */
export function markPublishPending(itineraryId: number): void {
  // Unique across TABS too: the serial is per tab, and two tabs can mint in
  // the same millisecond.
  storage.set(PENDING_KEY(itineraryId), `${Date.now()}-${++markerSerial}-${Math.random().toString(36).slice(2, 8)}`);
}

/** Remove the marker only if it is still the generation this observation
 *  started under. A newer publish's marker survives. */
function clearMarkerIfStill(itineraryId: number, generation: string | null): void {
  if (generation !== null && storage.get(PENDING_KEY(itineraryId)) === generation) {
    storage.remove(PENDING_KEY(itineraryId));
  }
}

/** Call after a private/link toggle: nothing to wait for. */
export function clearPublishPending(itineraryId: number): void {
  storage.remove(PENDING_KEY(itineraryId));
}

/** One status read; fires the event iff the plan is PUBLISHED (the fact, not
 *  the news) with a promoted_at not yet reported. Returns the state. */
export async function observePublish(itineraryId: number, planSlug?: string | null): Promise<string> {
  // The marker generation THIS observation is about, read before the request.
  const generation = storage.get(PENDING_KEY(itineraryId));
  const status = await checkShareStatus(itineraryId);
  const { publish } = status;
  const locks = webLocks();
  // No Web Locks → nothing can make the read-check-write once-only across
  // tabs → treat as seen: an undercount, never a double. (Not a fallback that
  // runs unlocked: that is exactly the double-fire.)
  if (publish.published && publish.promoted_at && locks) {
    const promotedAt = publish.promoted_at;
    await locks.request(`publish_seen:${itineraryId}`, async () => {
      const seen = seenSet(itineraryId);
      // A store that cannot be read cannot establish "unseen".
      if (seen === null || seen.has(promotedAt)) return;
      seen.add(promotedAt);
      if (!storage.set(SEEN_KEY(itineraryId), JSON.stringify(Array.from(seen)))) return;
      trackEvent('plan_shared', {
        method: 'publish_explore',
        itinerary_id: itineraryId,
        plan_slug: planSlug || undefined,
        first_publish: publish.first_publish === true,
      });
    });
  }
  // The marker outlives a recorded promotion while the NEWS is still live: a
  // re-publish of an already-published plan reports the standing plan's
  // promoted_at beside `state: publishing` for its replacement; clearing the
  // marker then would leave the replacement's promotion — landing after the
  // poll gives up, or after the tab closes — with nothing to observe it on
  // the next visit. Cleared only once the state is terminal.
  if (!['publishing', 'awaiting_owner'].includes(publish.state)) {
    clearMarkerIfStill(itineraryId, generation);
  }
  // Last, guarded, and after every decision above (decision 7).
  notifyStatus(itineraryId, status);
  return publish.state;
}

/** Poll until terminal or the schedule runs out. `sleep` is injectable. */
export async function watchPublish(
  itineraryId: number,
  planSlug?: string | null,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<string> {
  let state = 'publishing';
  for (const delay of POLL_SCHEDULE_MS) {
    await sleep(delay);
    try {
      state = await observePublish(itineraryId, planSlug);
    } catch {
      continue;
    }
    if (!['publishing', 'awaiting_owner'].includes(state)) return state;
  }
  return state;
}

/** On opening a plan with a toggle left "staged, not yet observed": look once. */
export async function checkPublishOnVisit(itineraryId: number, planSlug?: string | null): Promise<string | null> {
  const generation = storage.get(PENDING_KEY(itineraryId));
  if (!generation) return null;
  try {
    return await observePublish(itineraryId, planSlug);
  } catch (err) {
    // An OWNERSHIP refusal (the status GET is user-scoped: another account on
    // this browser left the marker) is final — clear it, or every visit pays
    // a refused request. A transient error keeps the marker for next time.
    const status = (err as { status?: number })?.status;
    if (status === 400 || status === 403) clearMarkerIfStill(itineraryId, generation);
    return null;
  }
}
